import React from 'react';
import { HelpCircle } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

/**
 * HOW A NUMBER IS CALCULATED, when someone asks (owner, 17 Oct 2026: a client
 * asked why Arrived beats Clicks and what Added to cart is a share of). At
 * rest a column shows its name; hovering it gives the one-line `tip`; its ?
 * opens this, which stays open; and the table's "How these numbers work"
 * opens every column's at once.
 *
 * A column's `help`:
 *   formula   the calculation in plain words ("Visits with an add-to-cart ÷ Arrived visits")
 *   counts    what is and is not counted, one short line each
 *   differs   why it can read differently from the ad platform (optional)
 *   example   (row) => the calculation on that row's own numbers, or null
 * The example is worked on the first row of the table that has one, so the
 * reader checks the rule against a number already on the screen.
 */

const nameOf = (r) => r?.label || r?.name || r?.campaign || '';

function exampleFor(help, rows) {
  if (!help?.example) return null;
  for (const r of rows || []) {
    const text = help.example(r);
    if (text) return { name: nameOf(r), text };
  }
  return null;
}

export function HelpBody({ label, tip, help, rows, className }) {
  const ex = exampleFor(help, rows);
  return (
    <div className={cn('space-y-3 text-[13.5px] leading-relaxed', className)}>
      <div>
        <p className="text-[14px] font-semibold text-foreground">{label}</p>
        {tip && <p className="mt-0.5 text-muted-foreground">{tip}</p>}
      </div>
      {help.formula && (
        <div>
          <p className="text-[12px] font-medium text-muted-foreground">How it&rsquo;s calculated</p>
          <p className="mt-1 rounded-md bg-muted/60 px-2.5 py-1.5 font-medium text-foreground">{help.formula}</p>
        </div>
      )}
      {help.counts?.length > 0 && (
        <div>
          <p className="text-[12px] font-medium text-muted-foreground">What counts</p>
          <ul className="mt-1 list-disc space-y-1 pl-4 text-foreground/90">{help.counts.map((t) => <li key={t}>{t}</li>)}</ul>
        </div>
      )}
      {help.differs?.length > 0 && (
        <div>
          <p className="text-[12px] font-medium text-muted-foreground">{help.differsTitle || 'Why it can differ from the ad platform'}</p>
          <ul className="mt-1 list-disc space-y-1 pl-4 text-foreground/90">{help.differs.map((t) => <li key={t}>{t}</li>)}</ul>
        </div>
      )}
      {ex && (
        <div className="rounded-md border border-[hsl(var(--border-light))] px-2.5 py-2">
          <p className="text-[12px] font-medium text-muted-foreground">Example from this table{ex.name ? `: ${ex.name}` : ''}</p>
          <p className="mt-0.5 font-medium tabular-nums text-foreground">{ex.text}</p>
        </div>
      )}
    </div>
  );
}

/** The ? beside a column name: its "How it's calculated" panel. */
export function ColumnHelp({ label, tip, help, rows }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" onClick={(e) => e.stopPropagation()} aria-label={`How ${label} is calculated`}
          title="How it's calculated"
          className="inline-flex size-4 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <HelpCircle className="size-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" onClick={(e) => e.stopPropagation()}
        className="max-h-[min(560px,calc(100vh-96px))] w-[min(380px,calc(100vw-32px))] overflow-y-auto p-4 text-left font-sans normal-case">
        <HelpBody label={label} tip={tip} help={help} rows={rows} />
      </PopoverContent>
    </Popover>
  );
}

/** "How these numbers work": every column's explanation, in one panel. */
export function TableHelp({ cols, rows, title = 'How these numbers work' }) {
  const [open, setOpen] = React.useState(false);
  const withHelp = (cols || []).filter((c) => c.help);
  if (!withHelp.length) return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)}
        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-input bg-card px-2.5 text-[13px] font-medium text-foreground transition-colors hover:border-border-strong dark:text-ink-100">
        <HelpCircle className="size-3.5" /> How it&rsquo;s calculated
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full overflow-y-auto p-6 sm:max-w-[520px]">
          <SheetHeader className="text-left">
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription>Every column in this table: what it counts, how it is worked out, and an example from the rows on screen.</SheetDescription>
          </SheetHeader>
          <div className="mt-5 divide-y divide-border">
            {withHelp.map((c) => (
              <HelpBody key={c.key} label={c.label} tip={c.tip} help={c.help} rows={rows} className="py-4 first:pt-0" />
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
